import { useState } from 'react';

import {
  TICKET_STATE_LABELS,
  ticketDisplaySubject,
  useCreateTicket,
  useTickets,
} from '@/lib/api/ticketHooks';
import { useClinicalPatientPrescriptions } from '@/lib/api/clinicalAdminHooks';

/** Patient-linked operational tickets and live Healthie tasks for CRM Overview. */
export function PatientTasksPanel({
  patientId,
  onOpenTicket,
}: {
  patientId: string;
  onOpenTicket?: (ticketId: string) => void;
}) {
  const tickets = useTickets({ patient_id: patientId });
  const prescriptions = useClinicalPatientPrescriptions(patientId);
  const [adding, setAdding] = useState(false);
  const clinicalTasks = (prescriptions.data?.tasks ?? []).filter((task) => !task.complete);

  return (
    <section className="border border-line rounded bg-raised overflow-hidden">
      <div className="px-15 py-12 border-b border-line flex items-center gap-8">
        <div className="min-w-0">
          <h2 className="type-label-md font-semibold">Patient tasks</h2>
          <p className="type-label-sm text-ink-muted">
            Support work and incomplete Healthie tasks linked to this patient.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="ml-auto type-label-sm px-10 py-7 rounded border border-accent text-accent bg-accent-wash"
        >
          + Add task
        </button>
      </div>

      <div className="divide-y divide-line-subtle">
        {(tickets.data?.tickets ?? []).map((ticket) => (
          <button
            key={ticket.id}
            type="button"
            onClick={() => onOpenTicket?.(ticket.id)}
            disabled={!onOpenTicket}
            className="w-full text-left px-15 py-10 flex items-start gap-10 hover:bg-sunken/40 disabled:hover:bg-transparent"
          >
            <span className="font-mono text-10 text-ink-muted shrink-0 mt-2">
              {ticket.ticket_number}
            </span>
            <span className="type-label-sm min-w-0 flex-1">
              <span className="block line-clamp-2">{ticketDisplaySubject(ticket)}</span>
              <span className="block text-10 text-ink-muted mt-2">
                Support · {TICKET_STATE_LABELS[ticket.state] ?? ticket.state}
              </span>
            </span>
          </button>
        ))}

        {clinicalTasks.map((task) => (
          <div key={task.id} className="px-15 py-10 flex items-start gap-10">
            <span className="font-mono text-10 text-ink-muted shrink-0 mt-2">HEALTHIE</span>
            <span className="type-label-sm min-w-0 flex-1">
              <span className="block whitespace-pre-wrap line-clamp-3">{task.content}</span>
              <span className="block text-10 text-ink-muted mt-2">
                Clinical task{task.due_date ? ` · due ${task.due_date}` : ''}
              </span>
            </span>
          </div>
        ))}

        {tickets.isLoading || prescriptions.isLoading ? (
          <div className="px-15 py-14 type-label-sm text-ink-muted">Loading tasks…</div>
        ) : (tickets.data?.tickets.length ?? 0) === 0 && clinicalTasks.length === 0 ? (
          <div className="px-15 py-14 type-label-sm text-ink-muted">
            No open tasks for this patient.
          </div>
        ) : null}
      </div>

      {adding && <NewPatientTaskModal patientId={patientId} onClose={() => setAdding(false)} />}
    </section>
  );
}

function NewPatientTaskModal({
  patientId,
  onClose,
}: {
  patientId: string;
  onClose: () => void;
}) {
  const create = useCreateTicket();
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [disposition, setDisposition] = useState('ops');

  const submit = () => {
    const normalizedTitle = title.trim();
    if (!normalizedTitle || create.isPending) return;
    create.mutate(
      {
        title: normalizedTitle,
        body: details.trim() || undefined,
        disposition,
        patient_id: patientId,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div className="fixed inset-0 z-[120]" role="dialog" aria-modal="true" aria-label="Add patient task">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,32rem)] bg-page border border-line rounded shadow-lg p-20 flex flex-col gap-12">
        <h2 className="type-label-md font-semibold">Add patient task</h2>
        <input
          autoFocus
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Task subject (required)"
          className="type-label-sm px-10 py-8 rounded border border-line bg-raised"
        />
        <textarea
          value={details}
          onChange={(event) => setDetails(event.target.value)}
          placeholder="Notes or acceptance criteria"
          rows={4}
          className="type-label-sm px-10 py-8 rounded border border-line bg-raised"
        />
        <select
          value={disposition}
          onChange={(event) => setDisposition(event.target.value)}
          className="type-label-sm px-10 py-8 rounded border border-line bg-raised"
          aria-label="Task disposition"
        >
          <option value="ops">Ops</option>
          <option value="clinical">Clinical</option>
          <option value="compliance">Compliance</option>
          <option value="unclassified">Needs triage</option>
        </select>
        {create.isError && (
          <div className="type-label-sm text-danger">Could not create the patient task.</div>
        )}
        <div className="flex justify-end gap-8">
          <button
            type="button"
            onClick={onClose}
            className="type-label-sm px-12 py-8 rounded border border-line"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!title.trim() || create.isPending}
            className="type-label-sm px-12 py-8 rounded bg-ink text-page disabled:opacity-40"
          >
            {create.isPending ? 'Adding…' : 'Add task'}
          </button>
        </div>
      </div>
    </div>
  );
}
